<script setup lang="ts">
  import { ref, onMounted, onBeforeUnmount } from "vue";
  import { useI18n } from "vue-i18n";
  import { IconArrowLeft, useNotification, Text, IconWireless, IconRfid } from "@ui-kit";
  import { usePowerRfid, useReadRfid } from "@plugins";
  import { TopBar, StatusBar } from "@src/components";
  import { useProfileOptions } from "@src/hooks";
  import { useGetWTFIR, useGetWTFIR_V2, WTFIRInputType, WTFIRTypeData, type Product, useLazyProductsLoad } from "@src/api";
  import { useCheckAlarming, type PairData } from "./useCheckAlarming";
  import { default as CheckAlarmingItem } from "./CheckAlarmingItem.vue";
  import { default as CheckAlarmingClearItem } from "./CheckAlarmingClearItem.vue";
  import { default as CheckAlarmingSection } from "./CheckAlarmingSection.vue";

  const emit = defineEmits<{ "close": [value: void] }>();

  const powerRfid = usePowerRfid();
  const readRfid = useReadRfid();
  const { execute: getWTFIR } = useGetWTFIR();
  const { execute: getWTFIR_V2 } = useGetWTFIR_V2();
  const { show } = useNotification();
  const { t } = useI18n();
  const { add, deleteItem, data: data_map, epcClearCount, alarmClearCount } = useCheckAlarming();
  const { execute: loadProducts } = useLazyProductsLoad();
  const { profile } = useProfileOptions();

  const pairs_map = ref<Map<string, Product & PairData>>(new Map());
  const withoutPairs_map = ref<Map<string, Product & PairData>>(new Map());
  const lastFoundPair = ref<Product | null>(null);
  const isScanningMapCompleted = ref(false);

  const handleScan = async (data: string) => {
    try {
      const resultWTFIR = await getWTFIR({ data, input_type: WTFIRInputType.rfid });

      if (resultWTFIR.isError.value) {
        throw { title: t("Не удалось получить данные"), type: "error" };
      }

      const { pair, type, exist } = await add(data, resultWTFIR.data.value.type_data);

      if (type === WTFIRTypeData.unknown) {
        throw { title: t("EPC или аларм не распознаны"), type: "error" };
      } else if (type === WTFIRTypeData.epcClear) {
        throw { title: t("Найден чистый EPC"), type: "error" };
      } else if (type === WTFIRTypeData.epcAlarmClear) {
        throw { title: t("Найден чистый Аларм"), type: "error" };
      }

      if (resultWTFIR.data.value.items.length === 0) {
        throw { title: t("Товар не найден"), type: "error" };
      }

      if (exist && pair?.originData && pair.secondaryData) {
        const existProduct = pairs_map.value.get(pair?.originData);
        pairs_map.value.delete(pair?.originData);
        pairs_map.value.set(pair?.originData, existProduct);
        lastFoundPair.value = existProduct;

        throw { title: t("Пара уже найдена"), type: "warning" };
      } else if (exist) {
        const existProduct = withoutPairs_map.value.get(pair?.originData);
        withoutPairs_map.value.delete(pair?.originData);
        withoutPairs_map.value.set(pair?.originData, existProduct);

        if (pair.originType === WTFIRTypeData.epc) {
          throw { title: t("EPC уже в списке"), type: "warning" };
        } else if (pair.originType === WTFIRTypeData.epcAlarm) {
          throw { title: t("Аларм уже в списке"), type: "warning" };
        }
      }

      if (!exist && pair.originData && pair.secondaryData) {
        const existProduct = withoutPairs_map.value.get(pair.originData);
        withoutPairs_map.value.delete(pair?.originData);
        pairs_map.value.set(pair?.originData, existProduct);
        lastFoundPair.value = existProduct;

        throw { title: t("Найдена пара"), type: "success" };
      }

      const product = await loadProducts({ item_id: [resultWTFIR.data.value.items.at(0)] });

      if (product.isError.value) {
        throw { title: t("Не удалось получить товар"), type: "error" };
      }

      withoutPairs_map.value.set(pair?.originData, { ...product.data.value.items[0], ...pair });
    } catch (e) {
      show({ title: e.title, type: e.type });
    }
  };

  onMounted(async () => {
    await readRfid.setMethod("singleShort");
    const powerResult = await powerRfid.set(profile.value.constants.rfid.power_once_read || 1);

    if (!powerResult) {
      show({ title: t("Не удалось установить мощность"), type: "error" });

      emit("close");
      return;
    }

    // собираем epc/alarm для загрузки данных
    const { pairs, withoutPair, all } = Array.from(data_map.value.values()).reduce<{
      pairs: PairData[];
      withoutPair: PairData[];
      all: string[];
    }>(
      (acc, item) => {
        if (![WTFIRTypeData.epc, WTFIRTypeData.epcAlarm].includes(item.originType as WTFIRTypeData)) {
          return acc;
        }

        if (item?.originData && item.secondaryData) {
          acc.pairs.push(item);
        } else {
          acc.withoutPair.push(item);
        }

        acc.all.push(item?.originData);

        return acc;
      },
      { pairs: [], withoutPair: [], all: [] },
    );

    if (all.length > 0) {
      const chunkSize = 100;
      const dataChunks = Array.from({ length: Math.ceil(all.length / chunkSize) }, (_, i) => all.slice(i * chunkSize, (i + 1) * chunkSize));

      // идём в wtfir для получения itemIds
      const wtfirResults = [];
      for (const chunk of dataChunks) {
        const resultChunk = await getWTFIR_V2({ data: chunk, input_type: WTFIRInputType.rfid });

        if (resultChunk.isError.value) {
          show({ title: t("Не удалось получить товары"), type: "error" });
          emit("close");
          return;
        }

        wtfirResults.push(...resultChunk.data.value.result);
      }

      const itemIds = wtfirResults.map((item) => item.item.at(0)).filter(Boolean);
      const itemIdChunks = Array.from({ length: Math.ceil(itemIds.length / chunkSize) }, (_, i) =>
        itemIds.slice(i * chunkSize, (i + 1) * chunkSize),
      );

      // загружаем товары по itemIds
      const productResults: Product[] = [];
      for (const chunk of itemIdChunks) {
        const productsChunk = await loadProducts({ item_id: chunk });

        if (!productsChunk.isError.value) {
          productResults.push(...productsChunk.data.value.items);
        }
      }

      const map_pairs = new Map(wtfirResults.map((item) => [item.data, item.item.at(0)]));
      const map_products = new Map(productResults.map((item) => [item.item_id, item]));

      // заполняем пары
      pairs.forEach((item) => {
        const product = map_products.get(map_pairs.get(item?.originData));

        pairs_map.value.set(item?.originData, { ...product, ...item });
      });

      // заполняем epc/alarm без пар
      withoutPair.forEach((item) => {
        const product = map_products.get(map_pairs.get(item?.originData));

        withoutPairs_map.value.set(item?.originData, { ...product, ...item });
      });
    }

    isScanningMapCompleted.value = true;

    readRfid.subscribe((data) => handleScan(data.value));
  });

  onBeforeUnmount(async () => {
    readRfid.unsubscribe();
    powerRfid.set(powerRfid.prevPower.value);
  });
</script>

<template>
  <div class="check-alarming-single" data-testid="check-alarming-single">
    <StatusBar />
    <TopBar :title="$t('Пара EPC + Аларм')" @click:left="emit('close')">
      <template #left>
        <IconArrowLeft />
      </template>
      <template #right>
        <div v-if="!isScanningMapCompleted" class="loading-indicator" data-testid="check-alarming-single-loading-indicator">
          <IconWireless style="color: var(--sf-base-icon-error)" />
        </div>
        <div v-else class="loading-indicator completed" data-testid="check-alarming-single-loading-indicator-completed">
          <IconRfid style="color: var(--sf-base-icon-success)" />
        </div>
      </template>
    </TopBar>
    <div class="check-alarming-single__content">
      <CheckAlarmingSection :title="$t('Неопознанные пары')" :placeholder="$t('Отсканируйте EPC или аларм')" data-testid="unknown-pairs">
        <Text v-if="withoutPairs_map.size === 0" font="body-regular-14" color="secondary">{{ $t("Отсканируйте EPC или аларм") }}</Text>
        <CheckAlarmingItem
          v-else
          v-for="item in Array.from(withoutPairs_map.values()).reverse()"
          :key="item.item_id"
          :imageUrl="item.image_url"
          :article="item.article"
          :hasEpc="item.originType === WTFIRTypeData.epc"
          :hasAlarm="item.originType === WTFIRTypeData.epcAlarm"
          :size="item.size"
          data-testid="unknown-item"
          @delete="
            () => {
              deleteItem(item?.originData);
              withoutPairs_map.delete(item?.originData);
            }
          "
        />
      </CheckAlarmingSection>
      <CheckAlarmingSection>
        <template #title>
          <div>
            <Text font="body-medium-16">{{ $t("Найденные пары EPC + Аларм") }}</Text>
            <Text v-if="pairs_map.size > 0" font="body-medium-16" color="secondary"> ({{ pairs_map.size }})</Text>
          </div>
        </template>
        <Text v-if="pairs_map.size === 0" font="body-regular-14" color="secondary">{{ $t("Тут будут корректные пары") }}</Text>
        <CheckAlarmingItem
          v-else
          v-for="item in Array.from(pairs_map.values()).reverse()"
          :key="item?.originData"
          :imageUrl="item.image_url"
          :article="item.article"
          :hasEpc="true"
          :hasAlarm="true"
          :size="item.size"
          data-testid="pair-item"
          @delete="
            () => {
              deleteItem(item?.originData);
              pairs_map.delete(item?.originData);
            }
          "
        />
      </CheckAlarmingSection>
    </div>
    <div class="check-alarming-single__footer">
      <div class="check-alarming-single__clear-items">
        <CheckAlarmingClearItem title="Чистые алармы" :count="alarmClearCount" />
        <CheckAlarmingClearItem title="Чистые EPC" :count="epcClearCount" />
      </div>

      <div v-if="lastFoundPair" class="check-alarming-single__footer-item">
        <CheckAlarmingItem
          :imageUrl="lastFoundPair.image_url"
          :article="lastFoundPair.article"
          :hasEpc="true"
          :hasAlarm="true"
          :size="lastFoundPair.size"
        />
        <div class="check-alarming-single__footer-info">
          <Text font="body-regular-12" color="secondary">
            {{ $t("последняя найденная пара") }}
          </Text>
          <Text font="body-regular-12" color="secondary">{{ $t("всего пар") }}:</Text>
          <Text font="body-regular-12">{{ pairs_map.size }}</Text>
        </div>
      </div>

      <div v-else class="check-alarming-single__footer-count">
        <Text font="body-regular-12" color="secondary">{{ $t("всего пар") }}:</Text>
        <Text font="body-regular-12">{{ pairs_map.size }}</Text>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .check-alarming-single {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    background-color: var(--sf-base-background-primary);
  }

  .check-alarming-single__content {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    gap: 28px;
    padding: 8px 16px;
  }

  .check-alarming-single__content :deep(.check-alarming-item) {
    padding: 12px;
    background-color: var(--sf-base-background-white);
    border-radius: 16px;
  }

  .check-alarming-single__footer {
    position: absolute;
    bottom: 16px;
    left: 16px;
    right: 16px;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: var(--sf-shadow-bot-m);
  }

  .check-alarming-single__clear-items {
    display: flex;
  }

  .check-alarming-single__clear-items :deep(.check-alarming-clear-item):not(:first-of-type) {
    border-left: 1px solid var(--sf-base-border-primary);
  }

  .check-alarming-single__footer-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 12px 8px;
    align-items: center;
    background-color: var(--sf-base-background-white);
  }

  .check-alarming-single__footer-item :deep(.check-alarming-item) {
    width: 100%;
  }

  .check-alarming-single__footer-count {
    display: flex;
    justify-content: center;
    gap: 4px;
    padding: 12px 12px 8px;
    background-color: var(--sf-base-background-white);
  }

  .check-alarming-single__footer-info {
    display: flex;
    width: 100%;
  }

  .check-alarming-single__footer-info :deep(.text):nth-of-type(2) {
    margin-left: auto;
  }

  .check-alarming-single__footer-info :deep(.text):nth-of-type(3) {
    margin-left: 4px;
  }

  .loading-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
  }

  /* Анимация моргания для индикатора загрузки (пока не загрузилось) */
  .loading-indicator:not(.completed) {
    animation: blink 1s ease-in-out infinite;
  }

  /* Убираем анимацию для завершённого состояния */
  .loading-indicator.completed {
    opacity: 1;
  }

  @keyframes blink {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.3;
    }
  }
</style>
